import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import useUpdateAutomation from '@/hooks/api/automations/useUpdateAutomation.ts';
import useRetrieveLlmModelOptions from '@/hooks/api/llmModels/useRetrieveLlmModelOptions.ts';
import { AutomationModelSettings, ModelPackageId } from '@/types/llmModels.ts';
import { buildModelSelectOptions } from '@/utils/llmModelSelectOptions.ts';
import {
  buildModelReasoningOptions,
  getAutomationReasoningUpdateValue,
  getReasoningSelectValue,
} from '@/utils/reasoningOptions.ts';
import { Stack } from '@mantine/core';
import { useMemo } from 'react';
import type { ChangeEvent } from 'react';

type Props = {
  automationId: string;
  modelSettings: AutomationModelSettings;
  enabled?: boolean;
};

const AutomationModelSettingsPanel = ({ automationId, modelSettings, enabled = true }: Props) => {
  const { data: modelOptions, isLoading, isError } = useRetrieveLlmModelOptions({ enabled });
  const { mutate: updateAutomation, isPending: isUpdating } = useUpdateAutomation();

  const modelById = useMemo(() => {
    return new Map(modelOptions?.models.map((model) => [model.model, model]) ?? []);
  }, [modelOptions?.models]);

  const packageOptions = useMemo(() => {
    const packages = modelOptions?.packages ?? [];
    const currentPackageId = modelSettings.model_package_id;
    const hasCurrentPackage = packages.some((modelPackage) => modelPackage.id === currentPackageId);
    const optionPackages = hasCurrentPackage
      ? packages
      : [
          {
            id: currentPackageId,
            display_name: currentPackageId,
            primary_model: modelSettings.primary_model,
            secondary_model: modelSettings.secondary_model,
            description: '',
            deprecated: true,
          },
          ...packages,
        ];

    return optionPackages.map((modelPackage) => ({
      value: modelPackage.id,
      label: modelPackage.deprecated
        ? `${modelPackage.display_name} (deprecated)`
        : modelPackage.display_name,
    }));
  }, [modelSettings, modelOptions?.packages]);

  const primaryModelOptions = useMemo(() => {
    return buildModelSelectOptions({
      modelIds: modelOptions?.primary_models ?? [],
      modelById,
      currentModelId: modelSettings.primary_model,
    });
  }, [modelSettings.primary_model, modelById, modelOptions?.primary_models]);

  const secondaryModelOptions = useMemo(() => {
    return buildModelSelectOptions({
      modelIds: modelOptions?.secondary_models ?? [],
      modelById,
      currentModelId: modelSettings.secondary_model,
    });
  }, [modelSettings.secondary_model, modelById, modelOptions?.secondary_models]);

  const primaryReasoningOptions = useMemo(() => {
    return buildModelReasoningOptions(
      modelById.get(modelSettings.primary_model),
      modelSettings.primary_reasoning_effort,
    );
  }, [modelSettings.primary_model, modelSettings.primary_reasoning_effort, modelById]);

  const secondaryReasoningOptions = useMemo(() => {
    return buildModelReasoningOptions(
      modelById.get(modelSettings.secondary_model),
      modelSettings.secondary_reasoning_effort,
    );
  }, [modelSettings.secondary_model, modelSettings.secondary_reasoning_effort, modelById]);

  const disabled = isLoading || isError || isUpdating;
  const error = isError ? 'Unable to load model options' : undefined;

  const updateModelPackage = (modelPackageId: string | null) => {
    if (!modelPackageId) return;
    updateAutomation({ id: automationId, model_package_id: modelPackageId as ModelPackageId });
  };

  const updatePrimaryModel = (modelId: string | null) => {
    if (!modelId) return;
    updateAutomation({ id: automationId, primary_model: modelId });
  };

  const updateSecondaryModel = (modelId: string | null) => {
    if (!modelId) return;
    updateAutomation({ id: automationId, secondary_model: modelId });
  };

  const updatePrimaryReasoning = (value: string | null) => {
    updateAutomation({
      id: automationId,
      primary_reasoning_effort: getAutomationReasoningUpdateValue(value),
    });
  };

  const updateSecondaryReasoning = (value: string | null) => {
    updateAutomation({
      id: automationId,
      secondary_reasoning_effort: getAutomationReasoningUpdateValue(value),
    });
  };

  const updateLargeContextEnabled = (event: ChangeEvent<HTMLInputElement>) => {
    updateAutomation({
      id: automationId,
      large_context_enabled: event.currentTarget.checked,
    });
  };

  return (
    <Stack gap={'sm'}>
      <StyledSelect
        label="Package"
        data={packageOptions}
        value={modelSettings.model_package_id}
        onChange={updateModelPackage}
        disabled={disabled}
        error={error}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Primary model"
        data={primaryModelOptions}
        value={modelSettings.primary_model}
        onChange={updatePrimaryModel}
        disabled={disabled}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Secondary model"
        data={secondaryModelOptions}
        value={modelSettings.secondary_model}
        onChange={updateSecondaryModel}
        disabled={disabled}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Primary reasoning"
        data={primaryReasoningOptions}
        value={getReasoningSelectValue(modelSettings.primary_reasoning_effort)}
        onChange={updatePrimaryReasoning}
        disabled={disabled}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Secondary reasoning"
        data={secondaryReasoningOptions}
        value={getReasoningSelectValue(modelSettings.secondary_reasoning_effort)}
        onChange={updateSecondaryReasoning}
        disabled={disabled}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSwitch
        label="Large context"
        description="Allows supported models to use a larger context budget. This can increase LLM cost."
        checked={modelSettings.large_context_enabled ?? false}
        onChange={updateLargeContextEnabled}
        disabled={disabled}
      />
    </Stack>
  );
};

export default AutomationModelSettingsPanel;
